// The lists a Jira connection is chosen from, before it is saved (#600), or while it is
// edited (#817).
//
// Read off `routes/integrations.py` (`list_jira_spaces`, `list_jira_work_types`) and
// `integrations/listing.py`. The credential is in the body because there is nowhere
// else for one that has not been saved; it is used for the one read and never comes
// back. A saved connection is named by its id instead, and the server opens its own. An answer's first three keys are a check's (`CheckRow`), so a list Jira would
// not give is read through the words a failed check is (`console/connectionHealth.ts`).
//
// **One page.** `more` says Jira has items this list does not hold; the flow then
// offers the value typed by hand rather than leaving a person whose space is on the
// next page with nowhere to go.

import type { Api } from "./http";
import type { JiraCredential } from "./integrations";

export const JIRA_SPACES_PATH = "/api/v1/integration-setup/jira/spaces";
export const JIRA_WORK_TYPES_PATH = "/api/v1/integration-setup/jira/work-types";

export interface JiraSpace {
  key: string;
  name: string;
}

export interface JiraWorkType {
  name: string;
}

/**
 * Who is asking Jira, and where: the flow's sign-in step, not yet a connection — or a
 * saved connection being edited (#817), listed with its own site and sealed pair, which
 * the browser never holds.
 */
export type JiraAsking = { base_url: string; credential: JiraCredential } | { connection_id: string };

/** What either list answers with, beside its items. */
export interface Listed {
  status: "passed" | "failed";
  last_status: number | null;
  last_detail: string;
  /** Jira's own count, or null where it gave none or the list failed. */
  total: number | null;
  /** Jira has items this list does not hold. */
  more: boolean;
}

export type ListedSpaces = Listed & { spaces: JiraSpace[] };
export type ListedWorkTypes = Listed & { work_types: JiraWorkType[] };

export const listJiraSpaces = (api: Api, asking: JiraAsking): Promise<ListedSpaces> =>
  api.post<ListedSpaces>(JIRA_SPACES_PATH, asking);

export const listJiraWorkTypes = (api: Api, asking: JiraAsking & { project_key: string }): Promise<ListedWorkTypes> =>
  api.post<ListedWorkTypes>(JIRA_WORK_TYPES_PATH, asking);

/** Reads both lists, for a flow that holds no `Api` of its own. */
export interface JiraLister {
  spaces(asking: JiraAsking): Promise<ListedSpaces>;
  workTypes(asking: JiraAsking & { project_key: string }): Promise<ListedWorkTypes>;
}
